import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Plus, Edit, Trash2, X, Save, Briefcase } from 'lucide-react'
import { apiFetch } from '../../config/api'
import PageHeader from '../../components/Dashboard/PageHeader'

interface JobPosting {
  id: number
  title: string
  description: string
  requirements: string | null
  location: string | null
  employment_type: string | null
  department: string | null
  status: 'Aktiv' | 'Inaktiv' | 'Geschlossen'
  created_at: string
  updated_at: string
}

export default function BewerbungManagement() {
  const [jobs, setJobs] = useState<JobPosting[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingJob, setEditingJob] = useState<JobPosting | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: '',
    location: '',
    employment_type: '',
    department: '',
    status: 'Aktiv' as 'Aktiv' | 'Inaktiv' | 'Geschlossen',
  })
  const formRef = useRef<HTMLDivElement>(null)

  const getAuthToken = () => {
    return localStorage.getItem('dashboard_token') || ''
  }

  const fetchJobs = async () => {
    try {
      setLoading(true)
      const response = await apiFetch('/jobs', {
        headers: {
          'Authorization': getAuthToken(),
        },
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setJobs(data.data || [])
        setError('')
      } else {
        setError(data.error || 'Fehler beim Laden der Stellenanzeigen')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Fetch jobs error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchJobs()
  }, [])

  const handleAddJob = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.title || !formData.description) {
      setError('Titel und Beschreibung sind erforderlich.')
      return
    }

    try {
      const response = await apiFetch('/jobs', {
        method: 'POST',
        headers: {
          'Authorization': getAuthToken(),
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          requirements: formData.requirements || null,
          location: formData.location || null,
          employment_type: formData.employment_type || null,
          department: formData.department || null,
          status: formData.status,
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setShowAddForm(false)
        resetForm()
        fetchJobs()
      } else {
        setError(data.error || 'Fehler beim Erstellen der Stellenanzeige')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Add job error:', err)
    }
  }

  const handleUpdateJob = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.title || !formData.description) {
      setError('Titel und Beschreibung sind erforderlich.')
      return
    }

    if (!editingJob) return

    try {
      const response = await apiFetch(`/jobs/${editingJob.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': getAuthToken(),
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          requirements: formData.requirements || null,
          location: formData.location || null,
          employment_type: formData.employment_type || null,
          department: formData.department || null,
          status: formData.status,
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setEditingJob(null)
        resetForm()
        fetchJobs()
      } else {
        setError(data.error || 'Fehler beim Aktualisieren der Stellenanzeige')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Update job error:', err)
    }
  }

  const handleDeleteJob = async (jobId: number) => {
    if (!confirm('Möchten Sie diese Stellenanzeige wirklich löschen?')) {
      return
    }

    try {
      const response = await apiFetch(`/jobs/${jobId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': getAuthToken(),
        },
      })

      const data = await response.json()

      if (response.ok && data.success) {
        fetchJobs()
      } else {
        setError(data.error || 'Fehler beim Löschen der Stellenanzeige')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Delete job error:', err)
    }
  }

  const startEdit = (job: JobPosting) => {
    setEditingJob(job)
    setFormData({
      title: job.title,
      description: job.description,
      requirements: job.requirements || '',
      location: job.location || '',
      employment_type: job.employment_type || '',
      department: job.department || '',
      status: job.status,
    })
    setShowAddForm(false)
    
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  const cancelEdit = () => {
    setEditingJob(null)
    resetForm()
  }

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      requirements: '',
      location: '',
      employment_type: '',
      department: '',
      status: 'Aktiv',
    })
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  return (
    <div className="w-full min-w-0 max-w-full">
      <PageHeader
        title="Stellenanzeigen"
        description="Offene Stellen anlegen und verwalten."
        actions={
          !showAddForm && !editingJob ? (
            <button
              onClick={() => {
                setShowAddForm(true)
                setEditingJob(null)
                resetForm()
                setTimeout(() => {
                  formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }, 100)
              }}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-brand-800 text-white text-sm font-medium hover:bg-brand-700"
            >
              <Plus className="h-4 w-4" />
              Neue Stellenanzeige
            </button>
          ) : undefined
        }
      />

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400"
        >
          {error}
        </motion.div>
      )}

      {/* Add/Edit Form */}
      {(showAddForm || editingJob) && (
        <motion.div
          ref={formRef}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 relative bg-gradient-to-br from-gray-900/80 to-gray-900/80 backdrop-blur-xl rounded-xl shadow-lg border border-white/10 overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />
          
          <div className="relative z-10 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white">
                {editingJob ? 'Stellenanzeige bearbeiten' : 'Neue Stellenanzeige erstellen'}
              </h2>
              <motion.button
                onClick={() => {
                  setShowAddForm(false)
                  cancelEdit()
                }}
                className="text-gray-400 hover:text-white transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="h-5 w-5" />
              </motion.button>
            </div>

            <form onSubmit={editingJob ? handleUpdateJob : handleAddJob} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Titel *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50"
                    required
                    placeholder="z.B. Servicekraft (m/w/d)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Abteilung
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50"
                    placeholder="z.B. Service, Küche"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Arbeitsort
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50"
                    placeholder="z.B. Krefeld"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Beschäftigungsart
                  </label>
                  <input
                    type="text"
                    value={formData.employment_type}
                    onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50"
                    placeholder="z.B. Vollzeit, Teilzeit, Minijob"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white focus:outline-none focus:border-brand-500/50"
                  >
                    <option value="Aktiv">Aktiv</option>
                    <option value="Inaktiv">Inaktiv</option>
                    <option value="Geschlossen">Geschlossen</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Beschreibung *
                </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={6}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50"
                    placeholder="Beschreibung der Stelle..."
                    required
                  />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Anforderungen
                </label>
                <textarea
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-500/50"
                  placeholder="Anforderungen und Qualifikationen..."
                />
              </div>

              <div className="flex items-center gap-4 pt-4">
                <motion.button
                  type="submit"
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-brand-600 to-brand-600 border border-brand-500/30 text-white rounded-xl hover:from-brand-500 hover:to-brand-500 transition-all duration-300 shadow-lg shadow-brand-600/20"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Save className="h-5 w-5" />
                  {editingJob ? 'Aktualisieren' : 'Erstellen'}
                </motion.button>
                <motion.button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false)
                    cancelEdit()
                  }}
                  className="px-6 py-3 bg-gray-800/50 border border-white/10 text-gray-300 rounded-xl hover:bg-gray-800 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Abbrechen
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      )}

      {/* Jobs Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative bg-gradient-to-br from-gray-900/80 to-gray-900/80 backdrop-blur-xl rounded-xl shadow-lg border border-white/10 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />
        
        <div className="relative z-10">
          {loading ? (
            <div className="p-12 text-center text-gray-300">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block"
              >
                <Briefcase className="h-8 w-8" />
              </motion.div>
              <p className="mt-4">Lade Stellenanzeigen...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="p-12 text-center">
              <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-300">Keine Stellenanzeigen gefunden</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 p-6">
              {jobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -4 }}
                  className="group relative bg-gradient-to-br from-gray-900/90 to-gray-900/90 backdrop-blur-xl rounded-xl shadow-lg border border-white/10 overflow-hidden hover:border-brand-500/30 transition-all duration-300"
                >
                  {/* Background Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-brand-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  {/* Job Info */}
                  <div className="relative z-10 p-5">
                    {/* Title */}
                    <h3 className="text-xl font-semibold text-white mb-3 line-clamp-2 group-hover:text-brand-400 transition-colors">
                      {job.title}
                    </h3>
                    
                    {/* Meta Info */}
                    <div className="space-y-2 mb-4">
                      {job.department && (
                        <div className="flex items-center gap-2 text-sm text-gray-300">
                          <span className="text-brand-400">Abteilung:</span>
                          <span>{job.department}</span>
                        </div>
                      )}
                      {job.location && (
                        <div className="flex items-center gap-2 text-sm text-gray-300">
                          <span className="text-brand-400">Ort:</span>
                          <span>{job.location}</span>
                        </div>
                      )}
                      {job.employment_type && (
                        <div className="flex items-center gap-2 text-sm text-gray-300">
                          <span className="text-brand-400">Art:</span>
                          <span>{job.employment_type}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Description */}
                    {job.description && (
                      <p className="text-sm text-gray-300 line-clamp-3 mb-4">
                        {job.description}
                      </p>
                    )}
                    
                    {/* Status Badge */}
                    <div className="flex items-center justify-between mb-4">
                      <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                        job.status === 'Aktiv'
                          ? 'bg-emerald-500/90 text-white'
                          : job.status === 'Geschlossen'
                          ? 'bg-gray-500/90 text-white'
                          : 'bg-yellow-500/90 text-black'
                      }`}>
                        {job.status}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatDate(job.created_at)}
                      </span>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                      <motion.button
                        onClick={() => startEdit(job)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-brand-500/10 hover:bg-brand-500/20 text-brand-400 rounded-lg transition-colors text-sm font-medium"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Edit className="h-4 w-4" />
                        Bearbeiten
                      </motion.button>
                      <motion.button
                        onClick={() => handleDeleteJob(job.id)}
                        className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title="Löschen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}







